import { useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Modal } from '../../../components/ui/Modal.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Textarea } from '../../../components/ui/Textarea.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { TagInput } from '../../../components/ui/TagInput.jsx';
import { aiApi } from '../../../api/ai.js';
import { ApiError } from '../../../api/client.js';
import { applyServerValidation } from '../../../lib/forms.js';
import { t } from '../../../lib/i18n.js';

const TONES = [
  { value: 'friendly', label: 'Amichevole' },
  { value: 'formal', label: 'Formale' },
  { value: 'playful', label: 'Giocoso' },
  { value: 'empathic', label: 'Empatico' },
  { value: 'technical', label: 'Tecnico' }
];

const EMPTY = {
  name: '',
  tone: 'friendly',
  tagline: '',
  language: 'it',
  signaturePhrases: [],
  values: [],
  guardrails: [],
  exampleConversations: [],
  customSystemPrompt: ''
};

export function PersonalityFormModal({ open, onClose, initial }) {
  const editing = Boolean(initial && initial._id);
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, control, setError, formState: { errors } } = useForm({ defaultValues: EMPTY });
  const examples = useFieldArray({ control, name: 'exampleConversations' });

  useEffect(() => {
    if (!open) return;
    reset(initial ? {
      name: initial.name || '',
      tone: initial.tone || 'friendly',
      tagline: initial.tagline || '',
      language: initial.language || 'it',
      signaturePhrases: initial.signaturePhrases || [],
      values: initial.values || [],
      guardrails: initial.guardrails || [],
      exampleConversations: initial.exampleConversations || [],
      customSystemPrompt: initial.customSystemPrompt || ''
    } : EMPTY);
  }, [open, initial, reset]);

  const mutation = useMutation({
    mutationFn: (values) => {
      const payload = { ...values };
      if (!payload.tagline) delete payload.tagline;
      if (!payload.customSystemPrompt) delete payload.customSystemPrompt;
      if (editing) {
        return aiApi.updatePersonality(initial._id, payload);
      }
      return aiApi.createPersonality(payload);
    },
    onSuccess: () => {
      toast.success(editing ? 'Personalità aggiornata.' : 'Personalità creata.');
      queryClient.invalidateQueries({ queryKey: ['ai', 'personalities'] });
      onClose();
    },
    onError: (err) => {
      const applied = applyServerValidation(err, setError);
      if (!applied) toast.error(err instanceof ApiError ? err.message : t('common.error'));
    }
  });

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      title={editing ? `Modifica ${initial?.name}` : 'Nuova personalità'}
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>{t('common.cancel')}</Button>
          <Button form="personality-form" type="submit" loading={mutation.isPending}>{t('common.save')}</Button>
        </>
      }
    >
      <form id="personality-form" className="flex flex-col gap-5" onSubmit={handleSubmit(mutation.mutate)} noValidate>
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input label="Nome" error={errors.name?.message} {...register('name', { required: 'Nome richiesto' })} />
          <Select label="Tono" options={TONES} error={errors.tone?.message} {...register('tone')} />
          <Input label="Lingua" hint="es. it, en" maxLength={8} {...register('language')} />
          <div className="sm:col-span-3">
            <Input label="Tagline" hint="una frase breve che riassume il modo di parlare" error={errors.tagline?.message} {...register('tagline')} />
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Controller
            control={control}
            name="signaturePhrases"
            render={({ field }) => (
              <TagInput label="Frasi firma" placeholder="es. Ciao amici animali!" value={field.value} onChange={field.onChange} />
            )}
          />
          <Controller
            control={control}
            name="values"
            render={({ field }) => (
              <TagInput label="Valori" placeholder="es. trasparenza, benessere animale" value={field.value} onChange={field.onChange} />
            )}
          />
          <Controller
            control={control}
            name="guardrails"
            render={({ field }) => (
              <TagInput label="Guardrail" placeholder="es. niente consigli medici" value={field.value} onChange={field.onChange} />
            )}
          />
        </section>

        <section>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-medium text-ink">Esempi di conversazione</p>
            <button
              type="button"
              className="text-xs text-brand hover:text-brand-hover"
              onClick={() => examples.append({ user: '', assistant: '' })}
            >
              + Aggiungi esempio
            </button>
          </div>
          {examples.fields.length === 0 ? (
            <p className="text-xs text-ink-muted">Nessun esempio. Aggiungerne aiuta a fissare il tono.</p>
          ) : (
            <div className="flex flex-col gap-3">
              {examples.fields.map((f, idx) => (
                <div key={f.id} className="rounded-md border border-border bg-surface p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Esempio {idx + 1}</p>
                    <button type="button" className="text-xs text-danger hover:underline" onClick={() => examples.remove(idx)}>
                      Rimuovi
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Textarea
                      label="Cliente"
                      rows={3}
                      error={errors.exampleConversations?.[idx]?.user?.message}
                      {...register(`exampleConversations.${idx}.user`, { required: 'Testo cliente richiesto' })}
                    />
                    <Textarea
                      label="Assistente"
                      rows={3}
                      error={errors.exampleConversations?.[idx]?.assistant?.message}
                      {...register(`exampleConversations.${idx}.assistant`, { required: 'Testo assistente richiesto' })}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <Textarea
          label="System prompt personalizzato (opzionale)"
          rows={5}
          hint="Sovrascrive la composizione automatica. Lasciare vuoto in caso di dubbio."
          error={errors.customSystemPrompt?.message}
          {...register('customSystemPrompt')}
        />
      </form>
    </Modal>
  );
}
